import { refreshAuthToken } from './client';
import { tokenLasting } from './tokenLifetime';
import { API_BASE_URL } from './config';
import { getStoredUser } from '@utils/userSession';

/**
 * Open an authenticated SSE stream.
 *
 * <p>Axios cannot stream, so every SSE endpoint is a raw `fetch` whose body is
 * handed to {@code useSseStream}. They also all need the same auth-refresh
 * retry: a stream opened with an almost-expired token would otherwise die on
 * its first 401 in the middle of work the server is still doing.
 *
 * <p>The caller owns the `signal` and closes the stream by aborting it.
 */
export const openAuthenticatedSse = async (
  path: string,
  init: RequestInit,
): Promise<Response> => {
  const tryOpen = async (token: string): Promise<Response> => {
    const headers = new Headers(init.headers);
    // JSON is listed too because a stream endpoint that rejects the request
    // answers with a JSON error body. Asking only for text/event-stream makes
    // Spring fail that negotiation, and the caller sees a bodyless 406 in
    // place of the reason it was actually refused.
    headers.set('Accept', 'text/event-stream, application/json');
    headers.set('Cache-Control', 'no-cache');
    headers.set('Authorization', `Bearer ${token}`);
    return fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  };

  const response = await tryOpen(await tokenLasting(getStoredUser()?.token ?? ''));
  if (response.status === 401 || response.status === 403) {
    const refreshed = await refreshAuthToken();
    if (refreshed) return tryOpen(refreshed);
  }
  return response;
};
